import { useMutation } from "@apollo/client";
import FileDownload from "@mui/icons-material/FileDownload";
import { Box, Button } from "@mui/material";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { StatusChip } from "components/StatusChip";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { CersDecisionStatus, StaffCersSubmittalsQuery } from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { GET_STAFF_CERS_SUBMITTALS } from "hooks/useStaffCersSubmittals";
import { useCallback, useEffect, useMemo, useState } from "react";

import { CersSubmittalRow, decisionStatusColor } from "./cersStatus";
import { SubmittalDetailDrawer } from "./SubmittalDetailDrawer";

const EXPORT_STAFF_CERS_SUBMITTALS = gql(`
  mutation ExportStaffCersSubmittals($search: String) {
    exportStaffCersSubmittals(search: $search) {
      success
      downloadUrl
      errorMsg
    }
  }
`);

// ── Stable, module-level config (V2 requires stable prop references) ──
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "submittedDate", sort: "desc" }];
// Only submittal-level text fields are filterable today. Module / status / date
// filtering lives on the child CersModuleDecision (needs an EXISTS subquery / range
// operator the omnisearch builder doesn't support) — deferred follow-up.
const FILTER_GROUPS: FilterGroup[] = [
  { name: "Facility", fields: ["facilityName"] },
  { name: "Organization", fields: ["organizationName"] },
  { name: "CUPA", fields: ["localRegulatorDistrict"] },
];

const getItems = (data: StaffCersSubmittalsQuery) => data.staffCersSubmittals.items ?? [];
const getCount = (data: StaffCersSubmittalsQuery) => data.staffCersSubmittals.count ?? 0;

function ExportButton({ search }: { search?: string }) {
  const alerts = useAlerts();
  const [exportMutation, { data, loading }] = useMutation(EXPORT_STAFF_CERS_SUBMITTALS);

  const result = data?.exportStaffCersSubmittals;

  useEffect(() => {
    if (result?.success) {
      alerts.success("Export ready");
    } else if (result?.errorMsg) {
      alerts.error(`Export failed: ${result.errorMsg}`);
    }
  }, [alerts, result?.success, result?.errorMsg]);

  useEffect(() => {
    if (result?.downloadUrl) {
      window.open(result.downloadUrl, "_blank");
    }
  }, [result?.downloadUrl]);

  // Exports the rows matching the current omnisearch string (V2 hands it to us).
  const handleClick = useCallback(() => {
    exportMutation({ variables: { search } });
  }, [exportMutation, search]);

  return (
    <Button
      size="medium"
      loading={loading}
      startIcon={<FileDownload />}
      onClick={handleClick}
      aria-label="Export"
    >
      Export
    </Button>
  );
}

export const Submittals = () => {
  useBreadcrumb({ label: "Submittals", to: "/staff/cers/submittals" });

  const [selectedSubmittal, setSelectedSubmittal] = useState<CersSubmittalRow | null>(null);

  const columns: OmnisearchGridColDef<CersSubmittalRow>[] = useMemo(
    () => [
      { field: "facilityName", headerName: "Facility", flex: 0.7 },
      { field: "organizationName", headerName: "Organization", flex: 0.7 },
      { field: "localRegulatorDistrict", headerName: "CUPA", flex: 0.5 },
      {
        field: "submittedDate",
        headerName: "Submitted Date",
        flex: 0.4,
        valueGetter: (_, row) => {
          if (!row.submittedDate) return "";
          return new Date(row.submittedDate).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            timeZone: "UTC",
          });
        },
      },
      { field: "submittedBy", headerName: "Submitted By", flex: 0.5, sortable: false },
      {
        field: "moduleDecisions",
        headerName: "Modules",
        flex: 1.5,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row.moduleDecisions?.length) return null;
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
              {row.moduleDecisions.map((decision) => (
                <StatusChip
                  key={decision.id}
                  label={prettyPrintEnumValue(decision.module)}
                  color={decisionStatusColor(decision.status)}
                />
              ))}
            </Box>
          );
        },
      },
      {
        field: "modulesDecidedCount",
        headerName: "Modules Decided",
        flex: 0.4,
        sortable: false,
        // "Decided" = CERS has reached a disposition on the module. Only Submitted
        // (queued) and UnderReview (CUPA still reviewing) are pending; every other
        // status — including NotApplicable, which is a terminal decision CERS made —
        // counts as decided.
        valueGetter: (_, row) =>
          row.moduleDecisions?.filter(
            (d) =>
              d.status !== CersDecisionStatus.Submitted &&
              d.status !== CersDecisionStatus.UnderReview
          ).length ?? 0,
      },
    ],
    []
  );

  const commandButtons = useCallback(
    ({ search }: { search: string }) => [<ExportButton key="cers-export" search={search} />],
    []
  );

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      search: omnisearch || undefined,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    []
  );

  const handleRowClick = useCallback(
    (params: GridRowParams<CersSubmittalRow>) => setSelectedSubmittal(params.row),
    []
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGridV2
        query={GET_STAFF_CERS_SUBMITTALS}
        getItems={getItems}
        getCount={getCount}
        persistenceKey="staff-cers-submittals-v2"
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialSortModel={INITIAL_SORT_MODEL}
        filterGroups={FILTER_GROUPS}
        columns={columns}
        commandButtons={commandButtons}
        buildVariables={buildVariables}
        onRowClick={handleRowClick}
        noRowsMessage="No CERS submittals found."
      />
      <SubmittalDetailDrawer
        submittal={selectedSubmittal}
        onClose={() => setSelectedSubmittal(null)}
      />
    </Box>
  );
};
